import { useState } from 'react';
import { Check, ChevronsUpDown, X, Users } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { UserAvatar } from '@/components/UserAvatar';

/**
 * Multi-select user dropdown.
 *
 * Props:
 * - users: [{ user_id, name, role?, email? }]
 * - selectedIds: string[]
 * - onChange: (ids: string[]) => void
 * - placeholder?: string
 * - testIdPrefix?: string
 */
export const UserMultiSelect = ({
  users = [],
  selectedIds = [],
  onChange,
  placeholder = 'Select assignees...',
  testIdPrefix = 'assignee',
}) => {
  const [open, setOpen] = useState(false);

  const selectedUsers = users.filter((u) => selectedIds.includes(u.user_id));

  const toggle = (userId) => {
    if (selectedIds.includes(userId)) {
      onChange(selectedIds.filter((id) => id !== userId));
    } else {
      onChange([...selectedIds, userId]);
    }
  };

  const remove = (userId, e) => {
    e.stopPropagation();
    onChange(selectedIds.filter((id) => id !== userId));
  };

  const roleLabel = (role, isMaster) => {
    if (isMaster) return 'Master Admin';
    if (role === 'admin') return 'Admin';
    return 'User';
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 h-9 px-3 rounded-md border border-input bg-background text-sm hover:bg-muted/50 transition-colors cursor-pointer"
            data-testid={`${testIdPrefix}-multiselect-trigger`}
          >
            <span className="flex items-center gap-2 text-muted-foreground truncate">
              <Users className="h-4 w-4 shrink-0" />
              {selectedIds.length === 0
                ? placeholder
                : `${selectedIds.length} user${selectedIds.length !== 1 ? 's' : ''} selected`}
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search user..." />
            <CommandList>
              <CommandEmpty>No user found.</CommandEmpty>
              <CommandGroup>
                {users.map((u) => {
                  const isSelected = selectedIds.includes(u.user_id);
                  return (
                    <CommandItem
                      key={u.user_id}
                      value={`${u.name} ${u.email || ''}`}
                      onSelect={() => toggle(u.user_id)}
                      data-testid={`${testIdPrefix}-option-${u.user_id}`}
                    >
                      <div
                        className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${
                          isSelected
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-muted-foreground/40'
                        }`}
                      >
                        {isSelected && <Check className="h-3 w-3" />}
                      </div>
                      <UserAvatar user={u} size="sm" className="mr-2" />
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="text-sm font-medium truncate">{u.name}</span>
                        {u.email && (
                          <span className="text-xs text-muted-foreground truncate">{u.email}</span>
                        )}
                      </div>
                      <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {roleLabel(u.role, u.is_master_admin)}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
            {users.length > 0 && (
              <div className="flex border-t p-1.5 gap-1">
                <button
                  type="button"
                  onClick={() => onChange(users.map((u) => u.user_id))}
                  className="flex-1 text-xs py-1.5 rounded hover:bg-muted transition-colors cursor-pointer font-medium text-primary"
                  data-testid={`${testIdPrefix}-select-all`}
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="flex-1 text-xs py-1.5 rounded hover:bg-muted transition-colors cursor-pointer font-medium text-muted-foreground"
                  data-testid={`${testIdPrefix}-clear-all`}
                >
                  Clear
                </button>
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>

      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-selected-pills`}>
          {selectedUsers.map((u) => (
            <Badge
              key={u.user_id}
              variant="secondary"
              className="gap-1.5 pl-1 pr-1 cursor-default"
              data-testid={`${testIdPrefix}-pill-${u.user_id}`}
            >
              <UserAvatar user={u} size="xs" />
              <span className="text-xs">{u.name}</span>
              <button
                type="button"
                onClick={(e) => remove(u.user_id, e)}
                className="rounded-full hover:bg-muted-foreground/20 p-0.5 cursor-pointer"
                aria-label={`Remove ${u.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserMultiSelect;
